<template>
	<el-container class="friend-page">
		<resizable-aside :default-width="260" :min-width="200" :max-width="500" storage-key="friend-aside-width">
			<div class="header">
				<el-input class="search-text" size="small" placeholder="搜索" v-model="searchText" @input="onSearchInput">
					<i class="el-icon-search el-input__icon" slot="prefix"></i>
				</el-input>
			</div>
			<!-- 筛选行 -->
			<div class="filter-row">
				<el-select v-model="filterStage" size="mini" placeholder="阶段" clearable @change="onFilterChange">
					<el-option label="新客户" value="new"></el-option>
					<el-option label="活跃" value="active"></el-option>
					<el-option label="沉默" value="silent"></el-option>
					<el-option label="流失" value="churned"></el-option>
				</el-select>
				<el-select v-model="filterTags" size="mini" placeholder="标签" multiple clearable @change="onFilterChange">
					<el-option v-for="t in friendStore.allTags" :key="t" :label="t" :value="t"></el-option>
				</el-select>
			</div>
			<el-scrollbar ref="friendScrollbar" class="friend-items">
				<!-- 阶段统计（仅全量加载后展示，分页过程中数字只反映已加载部分，避免误导） -->
				<div class="stage-bar" v-if="friendStore.loadedAll && friendStore.stageCounts">
					<span class="s-new">新{{ friendStore.stageCounts.new }}</span>
					<span class="s-active">活{{ friendStore.stageCounts.active }}</span>
					<span class="s-silent">沉{{ friendStore.stageCounts.silent }}</span>
					<span class="s-churned">流{{ friendStore.stageCounts.churned }}</span>
				</div>
				<div v-for="(friends, i) in friendValues" :key="i">
					<div class="letter">{{ friendKeys[i] }}</div>
					<div v-for="(friend) in friends" :key="friend.userID">
						<friend-item
							:friend="friend"
							:active="friend.userID === activeFriend.userID"
							@chat="onSendMessage(friend)"
							@click.native="onActiveItem(friend)">
						</friend-item>
					</div>
					<div v-if="i < friendValues.length - 1" class="divider"></div>
				</div>
				<!-- 分页加载状态（大客服滚动加载提示） -->
				<div class="load-more-bar" v-if="!friendStore.loadedAll">
					<span v-if="friendStore.friendLoading">加载中...</span>
					<span v-else>已加载 {{ friendStore.friends.length }}/{{ friendStore.total }}，滚动加载更多</span>
				</div>
				<div class="load-more-bar" v-else-if="friendStore.total > 0">已加载全部 {{ friendStore.total }} 位好友</div>
			</el-scrollbar>
		</resizable-aside>
		<el-container class="container">
			<div class="header" v-show="activeFriend.userID">
				{{ activeFriend.nickname }}
			</div>
			<div v-show="activeFriend.userID" class="detail-wrap">
				<el-scrollbar>
					<div class="friend-info">
						<head-image :size="120" :name="activeFriend.nickname" :url="activeFriend.avatar" radius="10%"
							@click.native="showFullImage()"></head-image>
						<div>
							<div class="info-item">
								<el-descriptions title="好友信息" class="description" :column="1">
									<el-descriptions-item label="昵称">{{ activeFriend.nickname }}</el-descriptions-item>
									<el-descriptions-item label="真实姓名">{{ activeFriend.realName || '-' }}</el-descriptions-item>
									<el-descriptions-item label="添加时间">{{ formatAddTime(activeFriend.addCustomerTime) }}</el-descriptions-item>
									<el-descriptions-item label="状态">{{ formatStatus(activeFriend.status) }}</el-descriptions-item>
									<el-descriptions-item label="企微VID">{{ activeFriend.vid }}</el-descriptions-item>
									<el-descriptions-item label="所属账号">{{ activeFriend.accountID }}</el-descriptions-item>
									<el-descriptions-item label="阶段">
										<el-tag :type="stageTagType(activeFriend.stage)" size="small">{{ stageLabel(activeFriend.stage) }}</el-tag>
									</el-descriptions-item>
								</el-descriptions>
							</div>
							<div class="btn-group">
								<el-button icon="el-icon-position" type="primary" size="small"
									@click="onSendMessage(activeFriend)">发消息</el-button>
							</div>
						</div>
					</div>
					<!-- 客户画像 -->
					<div class="profile-section" v-if="activeFriend.vid && activeFriend.userID">
						<customer-profile
							:profile="friendStore.activeProfile"
							:vid="activeFriend.vid"
							:userID="activeFriend.userID"
							:contact-name="activeFriend.nickname"
							:loading="friendStore.profileLoading"
							@tag-changed="onTagChanged">
						</customer-profile>
					</div>
				</el-scrollbar>
			</div>
		</el-container>
	</el-container>
</template>

<script>
import FriendItem from "../components/friend/FriendItem.vue";
import HeadImage from "../components/common/HeadImage.vue";
import ResizableAside from "../components/common/ResizableAside.vue";
import CustomerProfile from "../components/friend/CustomerProfile.vue";
import { pinyin } from 'pinyin-pro';
import { buildSingleConvID } from "../utils/conversation.js";

export default {
	name: "friend",
	components: {
		FriendItem,
		HeadImage,
		ResizableAside,
		CustomerProfile
	},
	data() {
		return {
			searchText: "",
			filterStage: "",
			filterTags: [],
			activeFriend: {}
		}
	},
	mounted() {
		// 上次会话残留筛选时：筛选结果整体替换过 friends，必须清空筛选并恢复默认完整列表，
		// 否则会因 loadedAll=true（空结果/末页）跳过加载，页面停留空白或筛选残留数据
		if (this.friendStore.hasActiveFilter) {
			this.friendStore.resetFilter();
			this.recoverDefaultFriends();
		} else if (!this.friendStore.loadedAll) {
			// 已全量加载（登录/AddGroupMember 补拉/此前滚动拉完）时跳过 reset 避免截断
			this.friendStore.loadFriend({ reset: true, pageSize: 200 }).catch(() => {});
		}
		// el-scrollbar（element-ui 2.15.x）不 emit scroll 事件，需对内部 wrap 绑定原生监听：
		// 滚动到底自动加载下一页
		this.$nextTick(() => {
			const wrap = this.$refs.friendScrollbar && this.$refs.friendScrollbar.$refs && this.$refs.friendScrollbar.$refs.wrap;
			if (wrap) {
				wrap.addEventListener('scroll', this.onScroll);
			}
		});
	},
	beforeDestroy() {
		const wrap = this.$refs.friendScrollbar && this.$refs.friendScrollbar.$refs && this.$refs.friendScrollbar.$refs.wrap;
		if (wrap) {
			wrap.removeEventListener('scroll', this.onScroll);
		}
		if (this._searchTimer) clearTimeout(this._searchTimer);
	},
	methods: {
		// 滚动接近底部时加载下一页（防重复触发由 store.friendLoading 保证）
		onScroll(e) {
			const el = e.target || e.srcElement;
			if (!el || this.friendStore.friendLoading || this.friendStore.loadedAll) return;
			const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
			if (dist < 150) {
				this.friendStore.loadFriend({ reset: false, pageSize: 200 }).catch(() => {});
			}
		},
		onActiveItem(friend) {
			this.activeFriend = friend;
			// 加载客户画像
			if (friend.vid && friend.userID) {
				this.friendStore.loadProfile(friend.vid, friend.userID).catch(() => {});
			}
		},
		onSendMessage(friend) {
			let existingChat = this.chatStore.findChatByFriend(friend.userID);
			let tenantID = this.userStore.userInfo.tenantID || 1;
			let vid = friend.vid || '';
			if (!existingChat && !vid) {
				this.$message.error('好友数据异常：缺少企业微信ID');
				return;
			}
			let conversationID = existingChat
				? existingChat.conversationID
				: buildSingleConvID(tenantID, vid, friend.userID);
			let chat = {
				conversationID: conversationID,
				convType: 'single',
				showName: friend.nickname,
				faceURL: friend.avatar,
				corpUserID: friend.vid,
				personalUserID: friend.userID,
				identity: friend.accountID || friend.vid || '',
				customer: friend.customer
			};
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(0);
			this.$router.push("/home/chat");
		},
		showFullImage() {
			if (this.activeFriend.avatar) {
				this.$eventBus.$emit("openFullImage", this.activeFriend.avatar);
			}
		},
		// 搜索输入防抖（300ms）：走服务端 keyword 过滤分页，避免本地过滤漏掉未加载的好友
		onSearchInput() {
			if (this._searchTimer) clearTimeout(this._searchTimer);
			this._searchTimer = setTimeout(() => {
				this.onFilterChange();
			}, 300);
		},
		// 恢复默认好友视图：优先全量（与登录加载一致，避免已全量持有的列表被 200 分页截断），
		// 全量失败（大客服超时等）退化为 200 分页首屏，滚动续拉
		recoverDefaultFriends() {
			this.friendStore.loadFriend({ reset: true, pageSize: 0 }).catch(() => {
				this.friendStore.loadFriend({ reset: true, pageSize: 200 }).catch(() => {});
			});
		},
		onFilterChange() {
			this.friendStore.setFilter({
				keyword: this.searchText,
				tags: this.filterTags,
				stage: this.filterStage
			});
			if (this.friendStore.hasActiveFilter) {
				// 有筛选条件：从第 1 页重新加载服务端筛选结果
				this.friendStore.loadFriend({ reset: true, pageSize: 200 }).catch(() => {});
			} else {
				// 筛选全部清空：恢复默认完整列表（全量），避免列表停留在筛选残留数据
				this.recoverDefaultFriends();
			}
		},
		onTagChanged() {
			// 标签变更后重新加载画像
			if (this.activeFriend.vid && this.activeFriend.userID) {
				this.friendStore.loadProfile(this.activeFriend.vid, this.activeFriend.userID).catch(() => {});
				this.friendStore.loadTags();
			}
		},
		formatStatus(status) {
			const map = { 9: '新好友', 2049: '已被对方删除', 8: '已被对方删除', 0: '已互删', 101: '已被拉黑' };
			return map[status] || '正常好友';
		},
		formatAddTime(timestamp) {
			if (!timestamp) return '-';
			return new Date(timestamp * 1000).toLocaleString('zh-CN');
		},
		stageLabel(s) {
			const m = { new: '新客户', active: '活跃', silent: '沉默', churned: '流失' };
			return m[s] || s || '-';
		},
		stageTagType(s) {
			const m = { new: 'success', active: '', silent: 'warning', churned: 'danger' };
			return m[s] || 'info';
		},
		firstLetter(strText) {
			let pyText = pinyin(strText, { toneType: 'none', type: 'normal' });
			return pyText[0];
		},
		isEnglish(character) {
			return /^[A-Za-z]+$/.test(character);
		}
	},
	computed: {
		friendMap() {
			let map = new Map();
			const allFriends = this.friendStore.friends;
			// 本地过滤需与服务端 SearchContacts 一致（nickname/real_name/user_id 三字段），
			// 否则服务端命中 realName/userID 的条目会被本地过滤隐藏，列表数与 total 矛盾
			const kw = this.searchText ? this.searchText.trim().toLowerCase() : '';
			allFriends.forEach((f) => {
				if (f.deleted) {
					return;
				}
				if (kw) {
					const hit = (f.nickname || '').toLowerCase().includes(kw)
						|| (f.realName || '').toLowerCase().includes(kw)
						|| (f.userID || '').toLowerCase().includes(kw);
					if (!hit) return;
				}
				let letter = this.firstLetter(f.nickname).toUpperCase();
				if (!this.isEnglish(letter)) {
					letter = "#"
				}
				if (map.has(letter)) {
					map.get(letter).push(f);
				} else {
					map.set(letter, [f]);
				}
			})
			let arrayObj = Array.from(map);
			arrayObj.sort((a, b) => {
				if (a[0] == '#' || b[0] == '#') {
					return b[0].localeCompare(a[0])
				}
				return a[0].localeCompare(b[0])
			})
			map = new Map(arrayObj.map(i => [i[0], i[1]]));
			return map;
		},
		friendKeys() {
			return Array.from(this.friendMap.keys());
		},
		friendValues() {
			return Array.from(this.friendMap.values());
		}
	}
}
</script>

<style lang="scss" scoped>
.friend-page {
	.header {
		height: 50px;
		display: flex;
		align-items: center;
		padding: 0 8px;
	}
	.filter-row {
		display: flex;
		gap: 4px;
		padding: 0 8px 6px 8px;
	}
	.stage-bar {
		display: flex;
		gap: 6px;
		padding: 4px 12px;
		font-size: 12px;
		span { cursor: pointer; }
		.s-new { color: #67c23a; }
		.s-active { color: #409eff; }
		.s-silent { color: #e6a23c; }
		.s-churned { color: #f56c6c; }
	}
	.friend-items {
		flex: 1;
		.letter {
			text-align: left;
			font-size: var(--im-larger-size-larger);
			padding: 5px 15px;
			color: var(--im-text-color-light);
		}
	}
	.load-more-bar {
		padding: 10px 0;
		text-align: center;
		font-size: 12px;
		color: var(--im-text-color-light);
	}
}

.container {
	display: flex;
	flex-direction: column;
	.header {
		height: 50px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0 12px;
		font-size: var(--im-font-size-larger);
		border-bottom: var(--im-border);
		box-sizing: border-box;
	}
	.detail-wrap {
		flex: 1;
		overflow: hidden;
	}
	.friend-info {
		display: flex;
		padding: 30px 40px 10px 40px;
		text-align: center;
		.info-item {
			margin-left: 20px;
			background-color: #ffffff;
			border: 1px #ddd solid;
		}
		.description {
			padding: 20px 20px 0 20px;
		}
	}
	.btn-group {
		text-align: left !important;
		padding: 10px 20px;
	}
	.profile-section {
		padding: 0 40px 20px 40px;
	}
}
</style>
